const TASK_STATUS_LABELS = {
  PENDING: "To Do",
  IN_PROGRESS: "In Progress",
  BLOCKED: "Blocked",
  DONE: "Done",
  FAILED: "Failed",
  SKIPPED: "Skipped",
  CANCELLED: "Cancelled",
};

const QUEUE_STATUS_LABELS = {
  ACTIVE: "Active",
  PAUSED: "Paused",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

const TASK_FILTERS = [
  { key: "all", label: "All" },
  { key: "todo", label: "To Do" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Done" },
  { key: "blocked_failed_cancelled", label: "Blocked/Failed/Cancelled" },
];

const KANBAN_COLUMNS = [
  { key: "todo", label: "To Do", statuses: ["PENDING"] },
  { key: "in_progress", label: "In Progress", statuses: ["IN_PROGRESS"] },
  { key: "done", label: "Done", statuses: ["DONE", "SKIPPED"] },
  {
    key: "blocked_failed_cancelled",
    label: "Blocked/Failed/Cancelled",
    statuses: ["BLOCKED", "FAILED", "CANCELLED"],
  },
];

const FILTER_GROUPS = {
  all: [],
  todo: ["PENDING"],
  in_progress: ["IN_PROGRESS"],
  done: ["DONE", "SKIPPED"],
  blocked_failed_cancelled: ["BLOCKED", "FAILED", "CANCELLED"],
};

const RESOLVED_TASK_STATUSES = new Set(["DONE", "FAILED", "SKIPPED", "CANCELLED"]);

function toIso(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function toMillis(value) {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function normalizeStatus(rawStatus) {
  return String(rawStatus || "")
    .trim()
    .toUpperCase();
}

export function getTaskStatusLabel(rawStatus) {
  const status = normalizeStatus(rawStatus);
  return TASK_STATUS_LABELS[status] || status || "Unknown";
}

export function getQueueStatusLabel(rawStatus) {
  const status = normalizeStatus(rawStatus);
  return QUEUE_STATUS_LABELS[status] || status || "Unknown";
}

export function normalizeQueueSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return { queue: null, mutation: null, error: null };
  }
  const queue = snapshot.queue && typeof snapshot.queue === "object" ? snapshot.queue : null;
  const mutation =
    snapshot.mutation && typeof snapshot.mutation === "object" ? snapshot.mutation : null;
  const error = snapshot.error && typeof snapshot.error === "object" ? snapshot.error : null;
  return { queue, mutation, error };
}

export function getQueueProgress(queue) {
  const tasks = Array.isArray(queue?.tasks) ? queue.tasks : [];
  const total = tasks.length;
  if (total === 0) {
    return {
      total: 0,
      resolved: 0,
      percentage: 0,
    };
  }
  const resolved = tasks.reduce((count, task) => {
    const status = normalizeStatus(task?.status);
    return count + (RESOLVED_TASK_STATUSES.has(status) ? 1 : 0);
  }, 0);
  return {
    total,
    resolved,
    percentage: Math.round((resolved / total) * 100),
  };
}

export function resolveCurrentTask(queue) {
  const tasks = Array.isArray(queue?.tasks) ? queue.tasks : [];
  const currentTaskId = String(queue?.currentTaskId || "").trim();
  if (currentTaskId) {
    const byId = tasks.find((task) => String(task?.taskId || "").trim() === currentTaskId);
    if (byId) return byId;
  }
  return tasks.find((task) => normalizeStatus(task?.status) === "IN_PROGRESS") || null;
}

export function getTaskFilters() {
  return TASK_FILTERS;
}

export function filterQueueTasks(tasks, filterKey) {
  const normalizedFilter = String(filterKey || "all").trim().toLowerCase();
  const activeStatuses = FILTER_GROUPS[normalizedFilter] || [];
  if (activeStatuses.length === 0) {
    return Array.isArray(tasks) ? tasks : [];
  }
  return (Array.isArray(tasks) ? tasks : []).filter((task) =>
    activeStatuses.includes(normalizeStatus(task?.status))
  );
}

export function sortQueueTasks(tasks) {
  return [...(Array.isArray(tasks) ? tasks : [])];
}

export function buildTaskQueueTimeline(queue) {
  const timeline = [];
  const tasks = Array.isArray(queue?.tasks) ? queue.tasks : [];
  const queueStatus = normalizeStatus(queue?.status);
  const queueCreatedAt = toIso(queue?.createdAt);
  const queueUpdatedAt = toIso(queue?.updatedAt);

  if (queueCreatedAt) {
    timeline.push({
      id: `queue-created-${queueCreatedAt}`,
      timestamp: queueCreatedAt,
      title: "Queue created",
      detail: queue?.goal || "",
      kind: "queue",
    });
  }

  if (queueStatus === "COMPLETED" && queueUpdatedAt) {
    timeline.push({
      id: `queue-completed-${queueUpdatedAt}`,
      timestamp: queueUpdatedAt,
      title: "Queue completed",
      detail: "",
      kind: "queue",
    });
  }

  if (queueStatus === "CANCELLED" && queueUpdatedAt) {
    timeline.push({
      id: `queue-cancelled-${queueUpdatedAt}`,
      timestamp: queueUpdatedAt,
      title: "Queue cancelled",
      detail: "",
      kind: "queue",
    });
  }

  tasks.forEach((task) => {
    const taskTitle = String(task?.title || "Task").trim() || "Task";
    const taskId = String(task?.taskId || Math.random());
    const status = normalizeStatus(task?.status);
    const createdAt = toIso(task?.createdAt);
    const startedAt = toIso(task?.startedAt);
    const completedAt = toIso(task?.completedAt);
    const skippedAt = toIso(task?.skippedAt);
    const updatedAt = toIso(task?.updatedAt);

    if (createdAt) {
      timeline.push({
        id: `${taskId}-created-${createdAt}`,
        timestamp: createdAt,
        title: "Task planned",
        detail: taskTitle,
        kind: "task",
      });
    }
    if (startedAt) {
      timeline.push({
        id: `${taskId}-started-${startedAt}`,
        timestamp: startedAt,
        title: "Task started",
        detail: taskTitle,
        kind: "task",
      });
    }
    if (status === "DONE" && completedAt) {
      timeline.push({
        id: `${taskId}-done-${completedAt}`,
        timestamp: completedAt,
        title: "Task completed",
        detail: task?.resultSummary || taskTitle,
        kind: "task",
      });
    }
    if (status === "SKIPPED" && skippedAt) {
      timeline.push({
        id: `${taskId}-skipped-${skippedAt}`,
        timestamp: skippedAt,
        title: "Task skipped",
        detail: task?.skipReason || taskTitle,
        kind: "task",
      });
    }
    if (status === "FAILED" && updatedAt) {
      timeline.push({
        id: `${taskId}-failed-${updatedAt}`,
        timestamp: updatedAt,
        title: "Task failed",
        detail: task?.failureReason || taskTitle,
        kind: "task",
      });
    }
    if (status === "BLOCKED" && updatedAt) {
      timeline.push({
        id: `${taskId}-blocked-${updatedAt}`,
        timestamp: updatedAt,
        title: "Task blocked",
        detail: task?.blockedReason || taskTitle,
        kind: "task",
      });
    }
    if (status === "CANCELLED" && updatedAt) {
      timeline.push({
        id: `${taskId}-cancelled-${updatedAt}`,
        timestamp: updatedAt,
        title: "Task cancelled",
        detail: taskTitle,
        kind: "task",
      });
    }
  });

  return timeline
    .filter((event) => Boolean(event.timestamp))
    .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
}

export function getKanbanColumns() {
  return KANBAN_COLUMNS;
}

export function groupTasksForKanban(tasks) {
  const sorted = sortQueueTasks(tasks);
  return KANBAN_COLUMNS.map((column) => {
    const rows = sorted.filter((task) =>
      column.statuses.includes(normalizeStatus(task?.status))
    );
    return {
      ...column,
      rows,
    };
  });
}

export {
  FILTER_GROUPS,
  KANBAN_COLUMNS,
  QUEUE_STATUS_LABELS,
  RESOLVED_TASK_STATUSES,
  TASK_STATUS_LABELS,
};
